import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CandidateRole } from "src/app/models/enum";
import { ManeuverOutcomeDraft, ManeuverOutcomeValue, OBComments } from "src/app/models/models";
import {
	buildOutcomeSummaryComment,
	isOutcomeSlotEnabled,
	MANEUVER_OUTCOME_NA_COMMENT,
	openOutcomeCommentModal,
	outcomeCommentField,
	outcomeRatingRequiresComment,
	OutcomeSlot,
} from "../../utils/ts1-rating-outcome-interaction";

@Component({
	selector: "app-ts1-rating-maneuver-outcome",
	templateUrl: "./ts1-rating-maneuver-outcome.component.html",
	styleUrls: ["./ts1-rating-maneuver-outcome.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingManeuverOutcomeComponent {
	@Input() pmCandidateName = "";
	@Input() pfCandidateName = "";
	@Input() pfCandidateId = "";
	@Input() pmCandidateId = "";
	/** Rating-form index 0 / 1 ids (left / right). Used for summary comment attribution. */
	@Input() leftCandidateId = "";
	@Input() rightCandidateId = "";
	@Input() maneuverLabel = "";
	@Input() outcome: ManeuverOutcomeDraft | null = null;
	/** Role per fixed candidate slot (0 = left, 1 = right). */
	@Input() candidateCrewRoles: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];
	@Input() hasSecondCandidate = false;

	@Output() outcomeChange = new EventEmitter<ManeuverOutcomeDraft>();
	@Output() outcomeComment = new EventEmitter<OBComments>();

	readonly ratingOptions: Array<number | "na"> = [1, 2, 3, 4, 5, "na"];

	private readonly allRows: Array<{
		slot: OutcomeSlot;
		role: string;
		isPrimary: boolean;
		side: "left" | "right";
	}> = [
		{ slot: "pfPrimary", role: "PF", isPrimary: true, side: "left" },
		{ slot: "pm", role: "PM", isPrimary: false, side: "left" },
		{ slot: "pmPrimary", role: "PM", isPrimary: true, side: "right" },
		{ slot: "pf", role: "PF", isPrimary: false, side: "right" },
	];

	private readonly singleCandidateRows: Array<{
		slot: OutcomeSlot;
		role: string;
		isPrimary: boolean;
		side: "left" | "right";
	}> = [
		{ slot: "pfPrimary", role: "PF", isPrimary: true, side: "left" },
		{ slot: "pm", role: "PM", isPrimary: false, side: "right" },
	];

	private isDialogOpen = false;

	constructor(private dialog: MatDialog) {}

	get displayRows(): Array<{
		slot: OutcomeSlot;
		role: string;
		isPrimary: boolean;
		side: "left" | "right";
	}> {
		return this.hasSecondCandidate ? this.allRows : this.singleCandidateRows;
	}

	slotLabel(row: { role: string; isPrimary: boolean }): string {
		return row.isPrimary ? `${row.role}(Primary)` : row.role;
	}

	isSlotInteractive(slot: OutcomeSlot): boolean {
		return isOutcomeSlotEnabled(slot, this.candidateCrewRoles, this.hasSecondCandidate);
	}

	select(slot: OutcomeSlot, value: ManeuverOutcomeValue): void {
		if (!this.outcome || !this.isSlotInteractive(slot)) return;

		const current = this.outcome[slot];
		const isSame = current === value;

		if (isSame && !outcomeRatingRequiresComment(value)) return;

		if (outcomeRatingRequiresComment(value)) {
			if (this.isDialogOpen) return;
			void this.promptCommentAndApply(slot, value, isSame);
			return;
		}

		this.applySelection(slot, value, "");
	}

	isSelected(slot: OutcomeSlot, value: ManeuverOutcomeValue): boolean {
		return this.outcome?.[slot] === value;
	}

	cellClasses(slot: OutcomeSlot, value: ManeuverOutcomeValue): Record<string, boolean> {
		if (!this.isSelected(slot, value)) return {};
		if (value === "na") {
			return { "ts1-maneuver-outcome__cell--selected": true, "ts1-maneuver-outcome__cell--rate-na": true };
		}
		if (value === "inc") {
			return { "ts1-maneuver-outcome__cell--selected": true, "ts1-maneuver-outcome__cell--rate-inc": true };
		}
		return {
			"ts1-maneuver-outcome__cell--selected": true,
			[`ts1-maneuver-outcome__cell--rate-${value}`]: true,
		};
	}

	optionLabel(value: ManeuverOutcomeValue): string {
		if (value === "na") return "N/A";
		if (value === "inc") return "Inc.";
		return String(value);
	}

	private async promptCommentAndApply(
		slot: OutcomeSlot,
		value: ManeuverOutcomeValue,
		allowSameValue: boolean,
	): Promise<void> {
		if (!this.outcome) return;
		if (!allowSameValue && this.outcome[slot] === value) return;

		this.isDialogOpen = true;
		const row = this.displayRows.find((r) => r.slot === slot);
		const commentKey = outcomeCommentField(slot);
		const existingComment = (this.outcome[commentKey] as string | undefined) ?? "";

		const result = await openOutcomeCommentModal(this.dialog, {
			maneuverLabel: this.maneuverLabel,
			slotLabel: row ? this.slotLabel(row) : slot,
			value,
			existingComment,
		});

		this.isDialogOpen = false;

		if (result?.comment != null) {
			this.applySelection(slot, value, result.comment);
		}
	}

	private applySelection(slot: OutcomeSlot, value: ManeuverOutcomeValue, instructorComment: string): void {
		if (!this.outcome) return;

		const commentKey = outcomeCommentField(slot);
		const next: ManeuverOutcomeDraft = {
			...this.outcome,
			[slot]: value,
			[commentKey]: outcomeRatingRequiresComment(value) ? instructorComment : undefined,
		};

		this.outcomeChange.emit(next);

		const row = this.displayRows.find((r) => r.slot === slot) ?? {
			slot,
			role: slot === "pm" || slot === "pmPrimary" ? "PM" : "PF",
			isPrimary: slot === "pfPrimary" || slot === "pmPrimary",
			side: "left" as const,
		};
		const candidateId = this.candidateIdForSlot(slot);
		if (!candidateId) return;

		const resolvedComment =
			value === "na"
				? MANEUVER_OUTCOME_NA_COMMENT
				: outcomeRatingRequiresComment(value)
					? instructorComment
					: "";

		this.outcomeComment.emit(
			buildOutcomeSummaryComment({
				maneuverLabel: this.maneuverLabel,
				maneuverCode: this.outcome.maneuverCode,
				slot,
				slotLabel: this.slotLabel(row),
				value,
				instructorComment: resolvedComment,
				candidateId,
			}),
		);
	}

	private candidateIdForSlot(slot: OutcomeSlot): string {
		const leftId = this.leftCandidateId || this.pfCandidateId;
		const rightId = this.rightCandidateId || this.pmCandidateId;
		if (!this.hasSecondCandidate) {
			return leftId || rightId;
		}
		if (slot === "pfPrimary" || slot === "pm") {
			return leftId;
		}
		return rightId;
	}
}
